package ui.app.visibility

import androidx.compose.runtime.*
import common.frontend.api.*
import provider.*
import tk.mallumo.compose.navigation.*
import tk.mallumo.compose.navigation.viewmodel.*
import tools.*


interface AppVisibilityScope {

    val vm: AppVisibilityListingVM
    val action: (AppVisibilityListingVM.Action) -> Unit

    val nav: Navigation
    val login: Io.User
    val args: AppVisibilityListingArgs
    val display: CommonDisplayProvider

    companion object {

        @Composable
        fun rememberScope(): AppVisibilityScope {

            val account = LocalAccountProvider.current
            val nav = LocalNavigation.current
            val args = LocalNavigationArgs.current
            val arg = args.rememberArgs(AppVisibilityListingArgs::class)
            val vm = nav.viewModel(AppVisibilityListingVM::class, arg.companyId.toString())
            val action = AppVisibilityListingVM.Action.rememberAction(vm)
            val display = LocalDisplayProvider.current
            return remember {
                object : AppVisibilityScope {
                    override val vm: AppVisibilityListingVM
                        get() = vm
                    override val action: (AppVisibilityListingVM.Action) -> Unit
                        get() = action
                    override val nav: Navigation
                        get() = nav
                    override val login: Io.User
                        get() = account.current!!
                    override val args: AppVisibilityListingArgs
                        get() = arg
                    override val display: CommonDisplayProvider
                        get() = display
                }.also {
                    vm.init(it)
                }
            }
        }
    }

    class Wrapper(
        val data: Io.ListingAppManager,
    ) {
        var change by mutableStateOf(false)

        var listingCompany: WrapperCompany = WrapperCompany(data.companyArr) {
            makeChange()
        }
            private set

        var listingVariants: WrapperVariant = WrapperVariant(data.variantArr)
            private set

        private fun makeChange() {
            change = listingCompany.state.any { it.change }
        }

        fun confirmChanges() {
            change = false
            listingCompany = WrapperCompany(data.companyArr) {
                makeChange()
            }
            listingVariants = WrapperVariant(data.variantArr)
        }
    }

    class WrapperVariant(
        items: MutableList<Io.ListingAppManager.ItemAppVariant>
    ) : EFieldList<Io.ListingAppManager.ItemAppVariant, WrapperVariant.VariantItem>(
        items = items,
        onBindEntry = {
            VariantItem(it)
        }
    ) {
        class VariantItem(
            override val data: Io.ListingAppManager.ItemAppVariant,
            override val key: String = data.id.toString(),
        ) : Entry<Io.ListingAppManager.ItemAppVariant>
    }

    class WrapperCompany(
        items: MutableList<Io.ListingAppManager.ItemCompany>,
        makeChange: () -> Unit
    ) : EFieldList<Io.ListingAppManager.ItemCompany, WrapperCompany.CompanyItem>(
        items = items,
        onBindEntry = {
            CompanyItem(it) {
                makeChange()
            }
        }
    ) {

        class CompanyItem(
            override val data: Io.ListingAppManager.ItemCompany,
            override val key: String = data.id.toString(),
            makeChange: () -> Unit
        ) : Entry<Io.ListingAppManager.ItemCompany> {

            val variants = CompanyAppVariant(data.variants) {
                makeChange()
            }

            val change get() = variants.state.any { it.change }
        }

        class CompanyAppVariant(
            items: MutableList<Io.ListingAppManager.ItemCompany.AppVariantEntry>,
            val onAnyChange: () -> Unit,
        ) : EFieldList<Io.ListingAppManager.ItemCompany.AppVariantEntry, CompanyAppVariant.Item>(
            items = items,
            onChange = { onAnyChange() },
            onBindEntry = {
                Item(it) {
                    onAnyChange()
                }
            }
        ) {
            class Item(
                override val data: Io.ListingAppManager.ItemCompany.AppVariantEntry,
                override val key: String = data.id.toString(),
                makeChange: () -> Unit
            ) : Entry<Io.ListingAppManager.ItemCompany.AppVariantEntry> {

                private var variantsOrigin = data.copy()

                val change get() = variantsOrigin.enabled != enabled.state

                val enabled: EFieldRegularBoolean = EFieldRegularBoolean(
                    property = data::enabled,
                    onChange = { makeChange() })
            }
        }
    }
}
